import { useEffect, useState } from "react";
import { fz, toastAward } from "@/lib/fanzone";
import { toast } from "sonner";
import { BookOpen, Target } from "lucide-react";

export default function TitansHistory() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(2000);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fz.history.today().then(({ data: d }) => {
      setData(d);
      if (d.completed) setResult({ year: d.year, diff: null, text: d.text });
    }).catch(() => {});
  }, []);

  if (!data) return <div className="titans-card p-6 text-titans-silver text-sm">Loading…</div>;

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data: r } = await fz.history.guess(Number(year));
      setResult(r);
      setData((d) => ({ ...d, completed: true, text: r.text }));
      if (r.diff === 0) toast.success("Exact year — historian!");
      else toast(`It was ${r.year} — you were ${r.diff} off`);
      if (r.award) toastAward(r.award);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Guess failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="titans-card p-5 md:p-6" data-testid="fz-history">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BookOpen size={16} className="text-titans-blue" />
          <span className="font-display text-xl uppercase">This Day in Titans History</span>
        </div>
        <span className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">
          {data.on_this_day ? "On this exact date" : "From the vault"}
        </span>
      </div>

      <div className="border-l-4 border-titans-red pl-4 mb-5">
        <div className="font-display text-2xl uppercase leading-tight" data-testid="fz-history-title">{data.title}</div>
        <p className="text-titans-silver font-body text-sm mt-2 leading-relaxed" data-testid="fz-history-text">{data.text}</p>
      </div>

      {!data.completed ? (
        <div data-testid="fz-history-guess-area">
          <div className="font-cond uppercase tracking-wider text-[10px] text-titans-blue mb-2 inline-flex items-center gap-1.5">
            <Target size={11} /> What year did this happen?
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <input
              type="range"
              min={1960}
              max={2026}
              value={year}
              onChange={(e) => setYear(e.target.value)}
              data-testid="fz-history-slider"
              className="flex-1 min-w-[180px] accent-[#C8102E]"
            />
            <div className="font-display text-4xl text-titans-blue tabular-nums w-24 text-center" data-testid="fz-history-year">{year}</div>
            <button onClick={submit} disabled={busy} data-testid="fz-history-submit" className="bg-titans-red hover:bg-titans-red-hover text-white px-5 py-2.5 font-cond uppercase tracking-wider text-xs disabled:opacity-50">
              Lock year
            </button>
          </div>
          <div className="font-cond uppercase tracking-wider text-[9px] text-titans-muted mt-2">
            Exact = 60 XP · within 1 = 40 · within 3 = 25
          </div>
        </div>
      ) : (
        result && (
          <div className="border-l-4 border-titans-blue bg-titans-blue/10 p-3" data-testid="fz-history-result">
            <span className="font-cond uppercase tracking-wider text-xs">
              It happened in <span className="font-display text-lg text-titans-blue">{result.year}</span>
              {result.diff !== null && result.diff !== undefined && (result.diff === 0 ? " — nailed it!" : ` — you were ${result.diff} year${result.diff === 1 ? "" : "s"} off`)}
              {" "}· Fresh history tomorrow
            </span>
          </div>
        )
      )}
    </div>
  );
}
